// Global Instructions Rule Applied!
// Read shapes for the CONSOLIDATED fund Ledger tab: workpaper trial-balance
// rows (view__fund_group_trial_balance — per-member closing cells +
// eliminations + consolidated opening/movement/closing) and the group GL
// drill-down rows (view__fund_group_gl_detail — the per-fund row shape plus
// the member fund identity and the intercompany flag).
// Statements reuse fund_statements.ts unchanged (byte-identical row shapes).
import { z } from 'zod'
import { validateWithZodSchema } from '../../core/utils/utilities.ts'
import { FundGlDetailRowSchema } from './fund_ledger.ts'

export const FundGroupLedgerMemberSchema = z
  .object({
    funds__id: z.string(),
    funds__name: z.string(),
    prog__is_primary: z.boolean().nullish()
  })
  .strict()

export type FundGroupLedgerMember = z.infer<typeof FundGroupLedgerMemberSchema>

export const FundGroupTrialBalanceRowSchema = z
  .object({
    acc_gl_accounts__id: z.string(),
    acc_gl_accounts__account_code: z.string().nullish(),
    acc_gl_accounts__account_name: z.string(),
    cfg_acc_account_types__label: z.string().nullish(),
    // always one entry per member fund (primary first); null closing =
    // account absent from that fund's chart
    prog__members: z.array(
      z
        .object({
          funds__id: z.string(),
          prog__closing: z.number().nullish(),
          prog__closing_fmt: z.string().nullish()
        })
        .strict()
    ),
    prog__eliminations: z.number(),
    prog__eliminations_fmt: z.string().nullish(),
    prog__opening: z.number(),
    prog__opening_fmt: z.string().nullish(),
    prog__debits: z.number(),
    prog__debits_fmt: z.string().nullish(),
    prog__credits: z.number(),
    prog__credits_fmt: z.string().nullish(),
    prog__closing: z.number(),
    prog__closing_fmt: z.string().nullish()
  })
  .strict()

export type FundGroupTrialBalanceRow = z.infer<typeof FundGroupTrialBalanceRowSchema>

export function validateFundGroupTrialBalanceRows(data: unknown): FundGroupTrialBalanceRow[] {
  try {
    return validateWithZodSchema(FundGroupTrialBalanceRowSchema, data, 'fund group trial balance row', {
      isArray: true
    })
  } catch (error) {
    throw new Error(`Fund group trial balance validation failed: ${error}`)
  }
}

export function validateFundGroupLedgerMembers(data: unknown): FundGroupLedgerMember[] {
  try {
    return validateWithZodSchema(FundGroupLedgerMemberSchema, data, 'fund group ledger member', {
      isArray: true
    })
  } catch (error) {
    throw new Error(`Fund group ledger member validation failed: ${error}`)
  }
}

export const FundGroupMemberFinYearSchema = z
  .object({
    funds__id: z.string(),
    funds__name: z.string(),
    acc_fin_years__fin_year: z.number().nullish(),
    acc_fin_years__closed: z.boolean().nullish()
  })
  .strict()

export type FundGroupMemberFinYear = z.infer<typeof FundGroupMemberFinYearSchema>

export const FundGroupGlDetailRowSchema = FundGlDetailRowSchema.extend({
  funds__id: z.string(),
  funds__name: z.string(),
  prog__intercompany: z.boolean()
}).strict()

export type FundGroupGlDetailRow = z.infer<typeof FundGroupGlDetailRowSchema>

export function validateFundGroupGlDetailRows(data: unknown): FundGroupGlDetailRow[] {
  try {
    return validateWithZodSchema(FundGroupGlDetailRowSchema, data, 'fund group GL detail row', {
      isArray: true
    })
  } catch (error) {
    throw new Error(`Fund group GL detail validation failed: ${error}`)
  }
}
